import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import { Plus, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending: "bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]",
  approved: "bg-[#F0FDF4] text-[#166534] border-[#22C55E]",
  rejected: "bg-[#FEF2F2] text-[#B91C1C] border-[#EF4444]",
};

export default function Leaves() {
  const { user, refresh } = useAuth();
  const { alert, confirm } = useDialog();
  const canReview = user.role === "admin" || user.role === "manager" || user.role === "hr";
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [tab, setTab] = useState("mine");
  const [showApply, setShowApply] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [form, setForm] = useState({ leave_type: "casual", start_date: "", end_date: "", reason: "", half_day: false, attachment_file_id: null });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/leaves/mine");
      setMine(r.data);
      if (canReview) {
        const a = await api.get("/leaves/all");
        setAll(a.data);
      }
      const t = await api.get("/leave-templates");
      setTemplates(t.data || []);
    } catch (err) {
      console.error("Failed to load leaves:", err);
    }
  };
  useEffect(() => { load(); }, [canReview]);

  const activeTpl = templates.find(t => t.id === selectedTpl);

  const applyTpl = (tid) => {
    setSelectedTpl(tid);
    const t = templates.find(x => x.id === tid);
    if (t) {
      setForm(f => ({
        ...f,
        leave_type: (t.allowed_types && t.allowed_types[0]) || t.leave_type || "casual",
        reason: "",
      }));
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/file", fd);
      setForm(f => ({ ...f, attachment_file_id: r.data.id }));
    } catch { /* ignore */ }
    finally { setUploading(false); }
  };

  const apply = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const body = { ...form };
      if (selectedTpl) body.template_id = selectedTpl;
      if (!body.attachment_file_id) delete body.attachment_file_id;
      await api.post("/leaves", body);
      setShowApply(false);
      setSelectedTpl("");
      setForm({ leave_type: "casual", start_date: "", end_date: "", reason: "", half_day: false, attachment_file_id: null });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const decide = async (id, status) => {
    const confirmed = await confirm({
      title: status === 'approved' ? 'Approve Leave Request' : 'Reject Leave Request',
      description: `Are you sure you want to ${status} this leave request?`,
      confirmText: status === 'approved' ? 'Approve' : 'Reject',
      cancelText: 'Cancel',
      variant: status === 'rejected' ? 'destructive' : 'default'
    });
    
    if (!confirmed) return;
    
    try {
      await api.post(`/leaves/${id}/decision`, { status, comment: "" });
      toast.success(`Leave ${status} successfully`);
      await load();
      await refresh();
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || `Failed to ${status} leave`;
      await alert({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
      console.error(`Error ${status} leave:`, err);
    }
  };

  const rows = tab === "mine" ? mine : all;

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">People / Leave</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Leave management</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Balance: <span className="font-mono-mini font-semibold">{user.leave_balance} days</span>
          </p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowApply(true)} data-testid="apply-leave-button">
          <Plus size={16} weight="bold" /> Apply Leave
        </button>
      </div>

      {canReview && (
        <div className="border-b border-border px-8 lg:px-12 py-4">
          <div className="flex gap-0 border border-border w-fit" data-testid="leave-tabs">
            <button onClick={() => setTab("mine")} className={`px-6 py-2 text-sm font-medium ${tab === "mine" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-muted"}`} data-testid="tab-my-leaves">
              My requests
            </button>
            <button onClick={() => setTab("all")} className={`px-6 py-2 text-sm font-medium border-l border-border ${tab === "all" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-muted"}`} data-testid="tab-pending-approvals">
              Team requests ({all.filter(l => l.status === "pending").length})
            </button>
          </div>
        </div>
      )}

      <div className="p-8 lg:p-12">
        <div className="border border-border" data-testid="leaves-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted">
                {tab === "all" && <th className="text-left px-4 py-3 eyebrow">Employee</th>}
                <th className="text-left px-4 py-3 eyebrow">Type</th>
                <th className="text-left px-4 py-3 eyebrow">Period</th>
                <th className="text-right px-4 py-3 eyebrow">Days</th>
                <th className="text-left px-4 py-3 eyebrow">Reason</th>
                <th className="text-left px-4 py-3 eyebrow">Status</th>
                {tab === "all" && <th className="px-4 py-3 eyebrow text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} className="border-b border-border hover:bg-muted/50" data-testid={`leave-row-${l.id}`}>
                  {tab === "all" && <td className="px-4 py-3 font-medium">{l.user_name}</td>}
                  <td className="px-4 py-3 capitalize">{l.leave_type}</td>
                  <td className="px-4 py-3 font-mono-mini">{l.start_date} → {l.end_date}</td>
                  <td className="px-4 py-3 text-right font-mono-mini">{l.days}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{l.reason || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block border px-2 py-0.5 text-xs uppercase font-mono-mini ${STATUS_STYLES[l.status]}`}>
                      {l.status}
                    </span>
                    {l.approvals && l.approvals.length > 0 && (
                      <div className="mt-1 flex gap-1 flex-wrap">
                        {l.approvals.map((a, i) => (
                          <span key={i} className={`text-[9px] font-mono-mini px-1 py-0.5 border ${STATUS_STYLES[a.status] || "border-slate-300"}`} title={`${a.approver_name} (${a.approver_role})`}>
                            {(a.approver_name || "?").split(" ")[0]}:{a.status[0].toUpperCase()}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  {tab === "all" && (
                    <td className="px-4 py-3 text-right">
                      {l.status === "pending" && (
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => decide(l.id, "approved")} className="px-3 py-1 text-xs bg-[#22C55E] text-white hover:bg-[#16A34A]" data-testid={`approve-${l.id}`}>Approve</button>
                          <button onClick={() => decide(l.id, "rejected")} className="px-3 py-1 text-xs bg-[#EF4444] text-white hover:bg-[#B91C1C]" data-testid={`reject-${l.id}`}>Reject</button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No leave requests</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Apply Leave modal */}
      {showApply && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowApply(false)}>
          <div className="bg-white border border-border w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="apply-leave-dialog">
            <div className="eyebrow mb-2">New request</div>
            <h2 className="font-display text-2xl font-bold mb-6">Apply for leave</h2>

            {/* Template picker */}
            {templates.length > 0 && (
              <div className="mb-4">
                <label className="eyebrow block mb-2">Choose Template (optional)</label>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => { setSelectedTpl(""); setForm(f => ({...f, half_day: false, attachment_file_id: null})); }}
                    className={`text-xs px-3 py-1.5 border ${!selectedTpl ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border"}`}>
                    None
                  </button>
                  {templates.map(t => (
                    <button key={t.id} type="button" onClick={() => applyTpl(t.id)}
                      className={`text-xs px-3 py-1.5 border ${selectedTpl === t.id ? "text-white" : ""}`}
                      style={{ backgroundColor: selectedTpl === t.id ? (t.color || "#0A0A0A") : "transparent",
                               borderColor: t.color || "#0A0A0A", color: selectedTpl === t.id ? "white" : (t.color || "inherit") }}
                      data-testid={`tpl-${t.id}`}>
                      {t.icon} {t.name}
                    </button>
                  ))}
                </div>
                {activeTpl && (
                  <div className="text-[11px] text-muted-foreground mt-2 border-l-2 pl-2" style={{ borderColor: activeTpl.color }}>
                    {activeTpl.description || "—"}
                    {activeTpl.min_notice_days > 0 && <div>Min notice: {activeTpl.min_notice_days} days</div>}
                    {activeTpl.max_days && <div>Max days: {activeTpl.max_days}</div>}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={apply} className="space-y-4">
              <div>
                <label className="eyebrow block mb-2">Type</label>
                <select value={form.leave_type} onChange={(e) => setForm({...form, leave_type: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="leave-type-select">
                  {activeTpl && activeTpl.allowed_types?.length > 0
                    ? activeTpl.allowed_types.map(t => <option key={t} value={t} className="capitalize">{t}</option>)
                    : <>
                        <option value="casual">Casual</option>
                        <option value="sick">Sick</option>
                        <option value="annual">Annual</option>
                        <option value="unpaid">Unpaid</option>
                      </>}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">Start</label>
                  <input type="date" required value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="leave-start-input" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">End</label>
                  <input type="date" required value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="leave-end-input" />
                </div>
              </div>
              {(!activeTpl || activeTpl.half_day_allowed !== false) && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.half_day} onChange={(e) => setForm({...form, half_day: e.target.checked})} data-testid="half-day-checkbox" />
                  Half day (only for single-day requests)
                </label>
              )}
              <div>
                <label className="eyebrow block mb-2">
                  Reason {activeTpl?.reason_required && <span className="text-[#FF3B30]">*</span>}
                </label>
                <textarea rows={3} value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})}
                  placeholder={activeTpl?.reason_placeholder || "Explain your reason…"}
                  className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="leave-reason-input" />
              </div>
              {activeTpl?.attachment_required && (
                <div>
                  <label className="eyebrow block mb-2">
                    Attachment <span className="text-[#FF3B30]">*</span>
                  </label>
                  <label className="flex items-center gap-2 border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                    <Paperclip size={14} weight="bold" />
                    <span>{form.attachment_file_id ? "Attached ✓" : (uploading ? "Uploading…" : "Choose file")}</span>
                    <input type="file" className="hidden" onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} data-testid="leave-attachment" />
                  </label>
                </div>
              )}
              {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowApply(false)} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
                <button type="submit" className="flex-1 btn-primary" data-testid="submit-leave">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
